// v5 navigation tree — labels/icons/ids copied verbatim from
// Alpaca Login Page/alpaca_intelligence_v5.html (design-system 6.1).
import { PAGE_MANIFEST, NAV_GROUP_DEFS, CATEGORY_DEFS } from '../features/performance/manifest';
import { NC_CATEGORIES } from '../features/notifications/data';

export type Badge = 'intel' | 'view' | 'new' | 'ai';

export interface RailItem { id: string; icon: string; title: string; color?: string; sep?: boolean; }

// One rail icon per top-level module, in the same order as the sidebar.
// Each id is the module group's FIRST form, so clicking the icon opens that
// form directly (and the matching sidebar group auto-expands). Icons mirror
// the sidebar group icons. Permission gating (canSee) hides any icon whose
// landing form the user can't access — identical to the sidebar group.
// Rail icons are colourful EMOJI (real coloured glyphs from the OS emoji font)
// — matches the original performance HTML design's rail.  AppShell renders any
// non-`ti-` icon string as an emoji (see NavIcon).
export const railItems: RailItem[] = [
  { id: 'home', icon: '🏠', title: 'Home' },
  { id: 'execdash', icon: '📊', title: 'Executive Dashboard' },
  { id: 'avainsight', icon: '✨', title: 'AVA Insight' },
  // Performance Analytics — rail opens the first dashboard; the group expands.
  { id: 'pa-exec-kpi', icon: '📈', title: 'Performance Analytics' },
  // Notification Center — rail opens the Overview; the group expands.
  { id: 'nc-overview', icon: '🔔', title: 'Notification Center' },
  { id: 'sep1', icon: '', title: '', sep: true },
  // Task Tracker group — Calendar / Job Progress / Test Progress (FB11)
  { id: 'taskcal',       icon: '📋', title: 'Task Tracker' },
  { id: 'bl-coc',        icon: '🗂️', title: 'Sample Backlog' },
  { id: 'schedtask',     icon: '📅', title: 'Sample Scheduling' },
  { id: 'sampreg',       icon: '📍', title: 'Sample Tracking' },
  { id: 'fielddata',     icon: '🧪', title: 'Sampling Management' },
  { id: 'subouttest',    icon: '🔄', title: 'Sample SubContract Tracking' },
  { id: 'preplqueue',    icon: '🧪', title: 'Sample Preparation' },
  { id: 'analysisq',     icon: '🔬', title: 'Analysis' },
  { id: 'sdmsval',       icon: '✅', title: 'Data Review' },
  { id: 'custreport',    icon: '📄', title: 'Reporting' },
  { id: 'trendanalysis', icon: '📉', title: 'QA/QC' },
  { id: 'acc-paymentterms', icon: '⚙️', title: 'Settings' },
  { id: 'maint-versionlog', icon: '🛠️', title: 'Maintenance' },
  { id: 'dc-collector',  icon: '🗄️', title: 'Data Center' },
];

export interface NavItem { id: string; label: string; icon: string; iconColor?: string; badge?: Badge; count?: number; }
/** A sub-group nested inside a top-level group — collapsible folder
 *  (e.g. Settings → Reporting → Regulatory Compliance → …). Sub-groups can
 *  themselves contain sub-groups, giving unlimited folder depth. */
export interface NavSubGroup {
  id: string;
  label: string;
  icon: string;
  iconColor?: string;
  badge?: Badge;
  /** Mixed children — clickable items and/or further nested sub-groups. */
  children: NavGroupChild[];
}
/** A child of a NavGroup can be: a clickable item, an uppercase sub-section
 *  label, or a fully-collapsible sub-group folder. */
export type NavGroupChild =
  | { kind: 'item'; item: NavItem }
  | { kind: 'subhead'; label: string }
  | { kind: 'subgroup'; subgroup: NavSubGroup };
export interface NavGroup { id: string; label: string; icon: string; iconColor?: string; badge?: Badge; items: NavItem[]; children?: NavGroupChild[]; }
export type NavEntry =
  | { kind: 'section'; label: string }
  | { kind: 'item'; item: NavItem }
  | { kind: 'sep' }
  | { kind: 'group'; group: NavGroup };

// ─── Performance Analytics group (built from the v8 manifest) ──────────────
// Categories → nav groups → dashboards live INSIDE the app's own left sidebar
// (expand "Performance Analytics" to see them — no second sidebar in content).
// Each dashboard nav id is `pa-<dashId>`; AppShell routes any `pa-*` page to
// PerformanceAnalyticsPage, which renders just that dashboard.  A dashboard that
// is cross-listed under several groups appears under each (same id, same page).
const PA_CAT_EMOJI: Record<string, string> = {
  'cat-exec': '📊', 'cat-labops': '🔬', 'cat-qa': '🛡️', 'cat-resources': '👥', 'cat-salesfin': '💰', 'cat-ai': '✨',
};
const PA_DASH_EMOJI: Record<string, string> = {
  'exec-kpi': '📊', 'sales-forecast': '🔮', 'lab-profit': '📈', 'sales-revclient': '💵', 'lab-revclient': '🧾',
  'sales-retention': '🤝', 'sales-atrisk': '🚨', 'sales-voltrend': '📉',
  'lab-throughput': '🔁', 'lab-volume': '📊', 'lab-mix': '🥧', 'lab-backlog': '📦', 'lab-queue': '🔢',
  'lab-tatdash': '⏱️', 'lab-duedatetracking': '📆', 'lab-tatalerts': '🔔', 'lab-tat': '⏰', 'lab-recurrence': '♻️',
  'lab-qcpass': '✅', 'lab-rerun': '🔄', 'lab-oos': '⚠️', 'lab-recovery': '🎯', 'lab-holdtime': '⏳',
  'lab-part11': '🖊️', 'lab-coc': '🔗', 'lab-rejection': '🚫', 'lab-capa': '🛠️', 'lab-rollback': '↩️', 'lab-reportcycle': '📄',
  'lab-analyst': '👤', 'lab-shift': '🕒', 'lab-util': '🖥️',
  'sales-repscorecard': '🏅', 'sales-clientcontribution': '🏢', 'sales-winrate': '🏆', 'sales-pipeline': '🔻',
  'sales-newclients': '➕', 'sales-quotetime': '🕗', 'sales-lostreasons': '👎',
  'revenue-explorer': '🧭', 'revenue-trend': '📈', 'revenue-concentration': '🔺',
  'sales-revtest': '💹', 'sales-revrep': '🧑‍💼', 'sales-recurring': '🔁', 'sales-yoy': '📈',
  'lab-costsample': '💲', 'lab-billing': '📑', 'sales-aov': '🛒', 'sales-tenure': '📅', 'sales-quota': '🎯',
  'sales-seasonal': '🗓️', 'sales-whitespace': '🧩', 'sales-renewals': '📜', 'sales-pricerealization': '🏷️',
};

function buildPerfAnalyticsGroup(): NavGroup {
  const items: NavItem[] = [{ id: 'pa-catalog', label: 'Dashboard Catalog', icon: '🔎' }];
  const seen = new Set<string>(['pa-catalog']);
  const catalogChild: NavGroupChild = { kind: 'item', item: { id: 'pa-catalog', label: 'Dashboard Catalog', icon: '🔎' } };

  // ⭐ My Dashboards — the first sub-module, listing the default-pinned
  // dashboards (manifest `favoriteDefault`).  Emoji per form, existing captions.
  const myDashChildren: NavGroupChild[] = PAGE_MANIFEST.filter((p) => p.favoriteDefault).map((p) => {
    const id = `pa-${p.id}`;
    const icon = PA_DASH_EMOJI[p.id] ?? '📊';
    if (!seen.has(id)) { seen.add(id); items.push({ id, label: p.label, icon, badge: p.badge }); }
    return { kind: 'item', item: { id, label: p.label, icon, badge: p.badge } };
  });
  const myDashboardsChild: NavGroupChild = { kind: 'subgroup', subgroup: { id: 'pa-mydash', label: 'My Dashboards', icon: '⭐', children: myDashChildren } };

  const categoryChildren: NavGroupChild[] = CATEGORY_DEFS.map((cat) => {
    // Dashboards whose ANY nav-group belongs to this category (cross-listed),
    // de-duplicated — the forms sit DIRECTLY under the category sub-module, with
    // no intermediate sub-section headers (emoji icon per form).
    const groupKeys = Object.keys(NAV_GROUP_DEFS).filter((gk) => NAV_GROUP_DEFS[gk]!.parent === cat.id);
    const seenInCat = new Set<string>();
    const dashChildren: NavGroupChild[] = [];
    for (const gk of groupKeys) {
      for (const p of PAGE_MANIFEST.filter((pp) => pp.navGroups.includes(gk))) {
        if (seenInCat.has(p.id)) continue;
        seenInCat.add(p.id);
        const id = `pa-${p.id}`;
        const icon = PA_DASH_EMOJI[p.id] ?? '📊';
        if (!seen.has(id)) { seen.add(id); items.push({ id, label: p.label, icon, badge: p.badge }); }
        dashChildren.push({ kind: 'item', item: { id, label: p.label, icon, badge: p.badge } });
      }
    }
    return { kind: 'subgroup' as const, subgroup: { id: `pa-${cat.id}`, label: cat.label, icon: PA_CAT_EMOJI[cat.id] ?? '📊', children: dashChildren } };
  }).filter((c) => c.subgroup.children.length > 0);
  // ⭐ My Dashboards first, then the category sub-modules, the Catalog last (8 total).
  return { id: 'grp-perfanalytics', label: 'Performance Analytics', icon: 'ti-chart-histogram', items, children: [myDashboardsChild, ...categoryChildren, catalogChild] };
}
const PERF_ANALYTICS_GROUP = buildPerfAnalyticsGroup();

// ─── Notification Center group (AI-only design module, demo data) ──────────
// Structured like Performance Analytics: a top-level group whose sub-items all
// render inside one host page (NotificationCenterPage), keyed by `nc-<subId>`.
// Visibility: all `nc-*` ids are ALWAYS_VISIBLE (see navPermissions.ts) — no
// ALPACA NavigationView / backend yet, identical to the `pa-*` dashboards.
const NC_CONFIG_ITEMS: NavItem[] = [
  { id: 'nc-overview',   label: 'Overview',           icon: 'ti-layout-dashboard' },
  { id: 'nc-rules',      label: 'Notification Rules', icon: 'ti-adjustments-horizontal' },
  { id: 'nc-categories', label: 'Rule Categories',    icon: 'ti-category-2' },
  { id: 'nc-recipients', label: 'Recipient Groups',   icon: 'ti-users-group' },
  { id: 'nc-templates',  label: 'Email Templates',    icon: 'ti-mail' },
  { id: 'nc-delivery',   label: 'Delivery Log',       icon: 'ti-mail-fast' },
];
// Browse-by-Category sub-module — one leaf per rule category (`nc-cat-<code>`),
// built from the SAME NC_CATEGORIES demo data the page uses so the labels / icons
// / order can't drift.  Clicking a category opens the Notification Rules list
// filtered to it (NotificationCenterPage handles the `cat-<code>` subId).
const NC_BROWSE_ITEMS: NavItem[] = NC_CATEGORIES.map((c) => ({ id: `nc-cat-${c.code}`, label: c.name, icon: c.icon }));
const NOTIFICATION_CENTER_GROUP: NavGroup = {
  id: 'grp-notifications', label: 'Notification Center', icon: 'ti-bell', iconColor: 'var(--brand-accent, var(--ai-orange))',
  // flat list — search index + pageTitles / navMeta / pageGroupId lookups
  items: [...NC_CONFIG_ITEMS, ...NC_BROWSE_ITEMS],
  // rendered tree — the six config forms, then a "Browse by Category" sub-module
  children: [
    ...NC_CONFIG_ITEMS.map((it) => ({ kind: 'item' as const, item: it })),
    { kind: 'subgroup', subgroup: { id: 'nc-browse', label: 'Browse by Category', icon: 'ti-category', children: NC_BROWSE_ITEMS.map((it) => ({ kind: 'item' as const, item: it })) } },
  ],
};

export const navTree: NavEntry[] = [
  // Section heading dropped — was "Intelligence", removed per user feedback
  // (no AI/Intel branding in module names).
  { kind: 'section', label: 'Overview' },
  { kind: 'item', item: { id: 'home', label: 'Home', icon: '🏠' } },
  { kind: 'item', item: { id: 'execdash', label: 'Executive Dashboard', icon: '📊' } },
  { kind: 'item', item: { id: 'avainsight', label: 'AVA Insight', icon: '✨' } },
  // Performance Analytics — categories → groups → dashboards (v8 manifest) live
  // in the app's own left sidebar; content shows just the selected dashboard.
  { kind: 'group', group: PERF_ANALYTICS_GROUP },
  { kind: 'sep' },
  // Notification Center now lives UNDER Settings (moved 2026-06-26, alphabetised
  // between Method and Reporting) — see the grp-settings children below.
  // ─── Task Tracker — pulled out of Sample Backlog into its own top-level
  // group (FB11 v2, 5/31/2026).  Task Calendar mirrors the Scheduled Task
  // Tracking page so the same calendar widget lives in this module too;
  // Job/Test Progress moved here from Sample Backlog. ───
  { kind: 'group', group: { id: 'grp-task-tracker', label: 'Task Tracker', icon: 'ti-clipboard-list', items: [
    { id: 'taskcal',  label: 'Task Calendar',          icon: 'ti-calendar-stats', iconColor: 'var(--ai-green)' },
    { id: 'jobprog',  label: 'Job Progress Tracking',  icon: 'ti-progress',       iconColor: 'var(--ai-blue)' },
    { id: 'testprog', label: 'Test Progress Tracking', icon: 'ti-progress-check', iconColor: 'var(--ai-green)' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-backlog', label: 'Sample Backlog', icon: 'ti-stack', items: [
    { id: 'bl-coc', label: '00 - Missing COC', icon: 'ti-alert-circle', iconColor: 'var(--ai-red)' },
    { id: 'bl-due', label: 'Backlog 00 - Due Samples', icon: 'ti-clock', iconColor: 'var(--ai-amber)' },
    { id: 'bl-track', label: 'Backlog 01 - Sample Tracking', icon: 'ti-map-pin' },
    { id: 'bl-transfer', label: 'Backlog 02 - Pending Transfer', icon: 'ti-transfer' },
    { id: 'bl-subout', label: 'Backlog 03 - Subout Tracking', icon: 'ti-transfer' },
    { id: 'bl-prep', label: 'Backlog 04 - Sample Preparation', icon: 'ti-test-pipe' },
    { id: 'bl-sdms', label: 'Backlog 05 - SDMS Result Entry', icon: 'ti-table' },
    { id: 'bl-review', label: 'Backlog 06 - Data Review', icon: 'ti-checkup-list' },
    // Numbers + order match the actual dbo.DashboardData titles (07 = Result
    // Approval, 08 = Reporting — the Review → Approve → Report workflow order).
    { id: 'bl-rappr', label: 'Backlog 07 - Result Approval', icon: 'ti-circle-check' },
    { id: 'bl-report', label: 'Backlog 08 - Reporting', icon: 'ti-report' },
    { id: 'icm01', label: 'ICM01 - Pending Orders', icon: 'ti-shopping-cart' },
    { id: 'icm02', label: 'ICM02 - Pending Validation', icon: 'ti-shield-check' },
    { id: 'icm03', label: 'ICM03 - Pending Receipt', icon: 'ti-receipt' },
    { id: 'icm05', label: 'ICM05 - Pending COA', icon: 'ti-certificate' },
  ] } },
  { kind: 'sep' },
  // ─── Sample lifecycle: 4 top-level groups (no "Samples" parent wrapper) ───
  { kind: 'group', group: { id: 'grp-sample-scheduling', label: 'Sample Scheduling', icon: 'ti-calendar', items: [
    { id: 'schedtask', label: 'Scheduled Task Tracking', icon: 'ti-calendar-stats' },
    { id: 'sampaloc',  label: 'Sampling Allocation',     icon: 'ti-grid-dots' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-sample-tracking', label: 'Sample Tracking', icon: 'ti-map-pin', items: [
    // flat list — search index + pageTitles / navMeta / pageGroupId lookups
    { id: 'sampreg',      label: 'Sample Registration',          icon: 'ti-clipboard-list' },
    { id: 'sampreceipt',  label: 'Sample Receipt Notification',  icon: 'ti-bell' },
    { id: 'sampphcheck',  label: 'Sample Container pH Check',    icon: 'ti-droplet' },
    { id: 'sample-in',        label: 'Sample In',        icon: 'ti-login-2' },
    { id: 'sample-out',       label: 'Sample Out',       icon: 'ti-logout-2' },
    { id: 'sample-disposal',  label: 'Sample Disposal',  icon: 'ti-trash' },
    { id: 'sample-inout-log', label: 'Sample In/Out Log', icon: 'ti-notebook' },
    { id: 'sample-location',  label: 'Sample Location',  icon: 'ti-map-pin' },
  ], children: [
    // rendered tree — the three flat forms, then the Barcode Sample Custody sub-module.
    // (When a group has `children`, the renderer shows ONLY `children`, so the flat
    //  forms MUST be repeated here as kind:'item' or they vanish from the sidebar.)
    { kind: 'item', item: { id: 'sampreg',     label: 'Sample Registration',         icon: 'ti-clipboard-list' } },
    { kind: 'item', item: { id: 'sampreceipt', label: 'Sample Receipt Notification', icon: 'ti-bell' } },
    { kind: 'item', item: { id: 'sampphcheck', label: 'Sample Container pH Check',   icon: 'ti-droplet' } },
    { kind: 'subgroup', subgroup: { id: 'sg-barcode-custody', label: 'Barcode Sample Custody', icon: 'ti-barcode', children: [
      { kind: 'item', item: { id: 'sample-in',        label: 'Sample In',        icon: 'ti-login-2' } },
      { kind: 'item', item: { id: 'sample-out',       label: 'Sample Out',       icon: 'ti-logout-2' } },
      { kind: 'item', item: { id: 'sample-disposal',  label: 'Sample Disposal',  icon: 'ti-trash' } },
      { kind: 'item', item: { id: 'sample-inout-log', label: 'Sample In/Out Log', icon: 'ti-notebook' } },
      { kind: 'item', item: { id: 'sample-location',  label: 'Sample Location',  icon: 'ti-map-pin' } },
    ] } },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-sampling-mgmt', label: 'Sampling Management', icon: '🧪', items: [
    { id: 'fielddata-entry',      label: 'Field Data Entry',      icon: 'ti-clipboard-data' },
    { id: 'fielddata-validation', label: 'Field Data Validation', icon: '🗂', iconColor: '#185fa5' },
    { id: 'fielddata-approval',   label: 'Field Data Approval',   icon: '✅' },
    { id: 'fielddata',    label: 'Field Data Tracking', icon: '📈' },
    { id: 'samptransfer', label: 'Sample Transfer',     icon: 'ti-arrows-exchange' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-subcontract', label: 'Sample SubContract Tracking', icon: 'ti-transfer', items: [
    { id: 'subouttest',  label: 'Subout Test Order',         icon: 'ti-file-invoice' },
    { id: 'suboutnotif', label: 'Subout Notification Queue', icon: 'ti-send' },
  ] } },
  { kind: 'sep' },
  // ─── Analysis lifecycle: 3 top-level groups (no "Analysis" parent wrapper) ─
  { kind: 'group', group: { id: 'grp-sample-prep', label: 'Sample Preparation', icon: '🧪', items: [
    { id: 'preplqueue',  label: 'Sample Prep Queue',   icon: 'ti-test-pipe' },
    { id: 'prepbatches', label: 'Sample Prep Batches', icon: 'ti-packages' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-analysis-core', label: 'Analysis', icon: 'ti-microscope', items: [
    { id: 'analysisq',  label: 'Analysis Queue',      icon: 'ti-microscope' },
    { id: 'resexpress', label: 'Result Entry Express', icon: 'ti-edit' },
    { id: 'qcbatches',  label: 'QC Batches',          icon: 'ti-chart-dots' },
    // SDMS Result Entry — OG nav item id 'Spreadsheet' (view 'SDMS'), under the
    // DataEntry/Analysis group right after QC Batches. Opens the SDMS spreadsheet
    // (blank; Retrieve a batch). The QC Batch form's "Open SDMS" opens the same
    // screen seeded with a batch.
    { id: 'sdms',       label: 'SDMS',                icon: 'ti-table' },
    { id: 'restrack',   label: 'Result Tracking',     icon: 'ti-list-check' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-data-review', label: 'Data Review', icon: 'ti-circle-check', items: [
    { id: 'sdmsval',  label: 'SDMS Result Validation', icon: 'ti-settings' },
    // SDMS Result Approval — batch-level (mirrors ALPACA RawDataLevel3BatchReview);
    // shares the Validation nav icon (SDMS package intent). Job-level 'rappr'
    // stays unchanged. (Merged 2026-07-07.)
    { id: 'sdmsappr', label: 'SDMS Result Approval',   icon: 'ti-settings' },
    // Legacy job-level "Result Validation" (ALPACA Model.xafml Data Review group,
    // ViewId QCBatch_ListView_ResultValidation_Filter) — the manual counterpart of
    // 'rappr', paired the same way SDMS Result Validation pairs with SDMS Result
    // Approval. Backend (AnalysisWriter.ValidateJobOneAsync/ValidateResultsOneAsync,
    // AnalysisReader.ResultValidationListAsync) and the list/detail UI
    // (AnalysisListPage kind="result-validation-manual", ResultValidationDetail
    // mode="validation") were already fully built; this nav entry + the AppShell
    // route below were the only missing piece (user report 2026-07-17).
    { id: 'rval',     label: 'Result Validation',      icon: 'ti-circle-check' },
    { id: 'rappr',    label: 'Result Approval',        icon: 'ti-circle-check' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-reporting', label: 'Reporting', icon: 'ti-report', items: [
    { id: 'custreport', label: 'Custom Reporting', icon: '📝' },
    // 'custreportbuilder' route is kept in AppShell for admin use but hidden
    // from the sidebar — user feedback 2026-06-06 ("not report build, this").
    { id: 'repval', label: 'Report Validation', icon: '📋' },
    // Report Approval — the 2nd-signature stage; shown only when approval is
    // required (gated in AppShell via reportApprovalRequired).
    { id: 'repappr', label: 'Report Approval', icon: 'ti-circle-check' },
    { id: 'repdel', label: 'Report Delivery', icon: '📦' },
    { id: 'reptrack', label: 'Report Tracking', icon: 'ti-files' },
    // Batch Reporting — query many jobs at once, one template per row, save all
    // to Report Validation (New Changes in Desgin prototype #screenBatch).
    { id: 'batchreport', label: 'Batch Reporting', icon: '📚' },
    { id: 'eddgen', label: 'EDD Report Generator', icon: 'ti-database-export' },
    { id: 'morlist',    label: 'MOR Reporting',       icon: 'ti-report-analytics' },
    { id: 'morpapp', label: 'MOR Pending Approval', icon: 'ti-clock-check' },
    { id: 'morpdel', label: 'MOR Pending Delivery', icon: 'ti-truck-delivery' },
    { id: 'msr', label: 'Monthly Summary Repository', icon: 'ti-archive' },
    // Ad Hoc Reporting — moved to the BOTTOM of the Reporting module per
    // user feedback 5/30/2026 #1.  Caption dropped "MOR" since the form
    // serves more than just monthly operations reporting.
    { id: 'morreport',  label: 'Ad Hoc Reporting', icon: 'ti-report-analytics' },
    // External Data Import — import external-lab results + limit monitoring.
    { id: 'edi-import', label: 'External Data Import', icon: 'ti-file-import' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-qaqc', label: 'QA/QC', icon: 'ti-chart-dots', items: [
    // QA/QC ▸ Control Charts — Levey-Jennings / range / sterility charts over QC
    // results, with the Westgard rule set and the signed QC record (layout
    // preview with static sample data, 2026-08-16).
    { id: 'control-charts', label: 'Control Charts', icon: 'ti-chart-histogram' },
    { id: 'mdlstudies', label: 'MDL Studies', icon: 'ti-chart-dots-3', iconColor: 'var(--brand-accent, var(--ai-orange))' },
    // QA/QC ▸ Training Records — employee training + qualification (layout
    // preview with static sample data, 2026-08-14).
    { id: 'training-records', label: 'Training Records', icon: 'ti-certificate' },
    { id: 'trendanalysis', label: 'Trend Analysis', icon: 'ti-chart-line' },
  ] } },
  { kind: 'sep' },
  // Settings → Reporting → Regulatory Compliance (and other future settings forms).
  // Data source: BTLIMS Windows app (Settings → DWRC-Category and Test).
  // ── Settings now appears BEFORE Data Center to match the user's preferred order.
  { kind: 'group', group: { id: 'grp-settings', label: 'Settings', icon: 'ti-settings',
    // True nested folders — matches BTLIMS pattern (Settings ▸ Accounting ▸ Payment Terms Setup):
    //   Settings ▸ Reporting ▸ Regulatory Compliance ▸ [Category | Sub Category | …]
    // User feedback 2026-05-31: "this new no need" — dropped the `badge:
    // 'new'` from every Settings menu entry.  They're no longer new.
    items: [  // legacy flat list (kept so the global nav search still finds these ids)
      { id: 'mor-template',  label: 'MOR Template Setup',       icon: 'ti-template' },
      // Settings ▸ Reporting ▸ MSR — Monthly Summary Repository config forms
      // (merged team package 2026-07-02; legacy MSRDataImportConfiguration +
      // MSRWaterSystem tables).
      { id: 'msr-data-import-config', label: 'MSR Data Import Config', icon: 'ti-file-import' },
      { id: 'msr-water-system',       label: 'Water System',           icon: 'ti-droplet' },
      { id: 'dwrc-cats',     label: 'Category',                icon: 'ti-category' },
      { id: 'dwrc-subcats',  label: 'Sub Category',            icon: 'ti-category-2' },
      { id: 'regcompliance', label: 'Category and Test Setup', icon: 'ti-shield-check' },
      // Data Package Review Checklist — Settings (legacy dbo.DataPackageCheckList).
      { id: 'data-package-checklist', label: 'Data Package Review Checklist', icon: 'ti-clipboard-check' },
      // Global lab workflow defaults (admin-only — visible to admin/service).
      { id: 'defaultsettings', label: 'Default Settings',      icon: 'ti-settings-cog' },
      { id: 'clientversion',   label: 'Client Version Control',  icon: 'ti-license' },
      // ALPACA's own application settings (shared dbo.AppConfig) — ported from the
      // legacy XAF Settings > Default Settings > App Config form.
      { id: 'alpacaappconfig', label: 'App Config',              icon: 'ti-adjustments-cog' },
      { id: 'screenautolock', label: 'Screen Auto Lock',        icon: 'ti-lock-clock' },
      // App-wide config: client timezone + per-form date source (admin-only — ADR-0008).
      // TEMPORARY (manager decision 2026-06-15): timezone conversion is disabled
      // during coexistence, so this Date & Time config has no effect — menu entry
      // HIDDEN.  The page + route remain; restore this line when UTC is reinstated.
      // { id: 'appconfig',     label: 'App Config',              icon: 'ti-adjustments-cog' },
      // Global per-form visibility (admin-only — controls what every other
      // form's visibility switch; gated via SERVICE_ONLY in navPermissions.ts).
      { id: 'formsetup',     label: 'Form Config',              icon: 'ti-forms' },
      // Global per-form audit-trail config (admin/service) — drives the audit
      // log + reason-on-edit/delete app-wide (standing rule 11).
      { id: 'audittrailcfg', label: 'Audit Trail Configuration', icon: 'ti-history' },
      // Bottle Sharing — Settings ▸ Analysis (legacy dbo.BottleSharing).
      { id: 'set-bottlesharing', label: 'Bottle Sharing', icon: 'ti-bottle' },
      // Settings ▸ SDMS — table-driven calculation config (2026-07-28). The
      // Formula Library (shared dbo.SpreadSheetBuilder_Formula, used by BOTH SDMS
      // templates and Result Entry) + the Result Entry assignment per exact
      // Test/Matrix/Method combo (dbo.AI_ResultEntryFormula).
      { id: 'reformula',  label: 'Formula Setup',    icon: 'ti-math-function' },
      { id: 'formulalib', label: 'Formula Library', icon: 'ti-math-symbols' },
      { id: 'custreportbuilder', label: 'Custom Report Builder', icon: 'ti-layout-grid' },
      // Settings ▸ Workflow Configuration ▸ ACT (ALPACA Custom Trigger) —
      // conditional prep-flow rules (layout preview, static data, 2026-08-13).
      { id: 'act-prepflow',  label: 'ACT',                     icon: 'ti-git-branch' },
      // Settings ▸ Workflow Configuration ▸ Workflow Engine (ADR-0011) — the
      // platform workflow service shared by all five modules.
      { id: 'workflow-engine', label: 'Workflow Engine',       icon: 'ti-sitemap' },
      // Service-only — gated via SERVICE_ONLY set in navPermissions.ts.
      // Hidden for everyone except `service` and admin users.
      { id: 'useractivity',  label: 'User Access Log',         icon: 'ti-user-check' },
      { id: 'exceptionlog',  label: 'Exception Log',           icon: 'ti-bug' },
      // Settings master-data CRUD modules (team delivery 2026-06-13).
      { id: 'set-methods',    label: 'Method',        icon: 'ti-check', iconColor: 'var(--ai-green)' },
      { id: 'set-holidays',   label: 'Holidays',      icon: 'ti-calendar-event' },
      // Settings → Dashboard → Assign Dashboard To User (ALPACA port).
      { id: 'set-assign-dashboard', label: 'Assign Dashboard To User', icon: 'ti-layout-dashboard' },
      // Settings master-data lookups (merged team package 2026-07-02) — flat list
      // for global nav search (also nested under Settings ▸ Utility subgroups).
      { id: 'set-method-category',   label: 'Method Category',   icon: 'ti-category' },
      { id: 'set-qc-root-role',      label: 'QC Root Role',      icon: 'ti-hierarchy-2' },
      { id: 'set-qc-source',         label: 'QC Source',         icon: 'ti-source-code' },
      { id: 'set-sample-category',   label: 'Sample Category',   icon: 'ti-category-2' },
      { id: 'set-storage',           label: 'Storage',           icon: 'ti-archive' },
      { id: 'set-units',             label: 'Units',             icon: 'ti-ruler-2' },
      { id: 'set-report-category',   label: 'Report Category',   icon: 'ti-category' },
      { id: 'set-report-type',       label: 'Report Type',       icon: 'ti-report' },
      { id: 'set-crm-category',      label: 'Category',          icon: 'ti-category' },
      { id: 'set-request-category',  label: 'Request Category',  icon: 'ti-category' },
      { id: 'set-cmdp-sample-category',    label: 'CMDP Sample Category',        icon: 'ti-category-2' },
      { id: 'set-cmdp-method-param-match', label: 'CMDP Method and Param Match', icon: 'ti-list-check' },
      { id: 'set-asbestos-fibers',        label: 'Asbestos Fibers',        icon: 'ti-line-dashed' },
      { id: 'set-fiber-types',            label: 'Fiber Type',             icon: 'ti-line' },
      { id: 'set-fiber-type-values',      label: 'Fiber Type Values',      icon: 'ti-table' },
      { id: 'set-materials',              label: 'Materials',              icon: 'ti-box-multiple' },
      { id: 'set-non-asbestos-fibers',    label: 'Non-Asbestos Fibers',    icon: 'ti-line-dashed' },
      { id: 'set-non-fibrous-components', label: 'Non-Fibrous Components',  icon: 'ti-components' },
      { id: 'set-other-components',       label: 'Other Components',       icon: 'ti-puzzle' },
      { id: 'set-result-range',           label: 'Result Range',           icon: 'ti-arrows-horizontal' },
      { id: 'set-unconventional-analysis', label: 'Unconventional Analysis', icon: 'ti-flask-off' },
      // Contract Lab — Settings ▸ Analysis (legacy dbo.SubOutContractLab).
      { id: 'set-contractlab',            label: 'Contract Lab',           icon: 'ti-building-factory-2' },
      { id: 'acc-paymentterms', label: 'Payment Terms', icon: 'ti-cash' },
      { id: 'acc-paymentterms-setup', label: 'Payment Terms Setup', icon: 'ti-receipt' },
      { id: 'acc-region',     label: 'Region',        icon: 'ti-map-pin' },
      // External Data Import — Permit Libraries + feature config (Phase 1/2).
      { id: 'edi-permit-types',   label: 'Permit Types',   icon: 'ti-tags' },
      { id: 'edi-permit-library', label: 'Permit Library', icon: 'ti-shield-half' },
      { id: 'edi-permit-setup',   label: 'Permit Setup',   icon: 'ti-adjustments-alt' },
      // Settings ▸ Permit Library New (Lifecycle v5 rebuild) — flat-catalog entry
      // so the global nav search finds it.
      { id: 'pln-permit-type',    label: 'Permit Type New', icon: 'ti-tags' },
      { id: 'pln-limit-type',     label: 'Limit Type',      icon: 'ti-ruler-2' },
      { id: 'edi-sheet-mapping',  label: 'Sheet Mapping',    icon: 'ti-table-options' },
      { id: 'edi-limits',         label: 'MCL & SCL Limits', icon: 'ti-ruler-2' },
      // System Management + Analysis Department Chain (merged 2026-06-26) — flat list for nav search.
      { id: 'adc-chain',      label: 'Analysis Department Chain', icon: 'ti-sitemap' },
      { id: 'sm-division',    label: 'Division',         icon: 'ti-building-community' },
      { id: 'sm-department',  label: 'Department',       icon: 'ti-sitemap' },
      { id: 'sm-position',    label: 'Position',         icon: 'ti-id-badge-2' },
      { id: 'sm-state',       label: 'State',            icon: 'ti-map-2' },
      { id: 'sm-country',     label: 'Country',          icon: 'ti-flag' },
      { id: 'sm-city',        label: 'City',             icon: 'ti-building' },
      { id: 'sm-language',    label: 'Current Language', icon: 'ti-language' },
      { id: 'sm-timezones',   label: 'Time Zones',       icon: 'ti-clock-hour-4' },
      // Notification Center config + browse leaves (moved under Settings 2026-06-26)
      // — kept in the flat list so global nav search still finds every nc-* id.
      ...NC_CONFIG_ITEMS, ...NC_BROWSE_ITEMS,
    ],
    children: [
      // Settings forms in ALPHABETICAL order (by label) — sub-groups sorted too.
      // Utility ▸ Accounting — Payment Terms + Region master-data CRUD.
      // Settings ▸ Workflow Configuration — ACT (ALPACA Custom Trigger).
      // Position here is irrelevant: the alphabetical sort below orders every
      // top-level Settings entry, and "W" lands it last.
      { kind: 'subgroup', subgroup: {
          id: 'sg-workflow-config', label: 'Workflow Configuration', icon: 'ti-route',
          children: [
            { kind: 'item', item: { id: 'act-prepflow', label: 'ACT', icon: 'ti-git-branch' } },
            // Workflow Engine (ADR-0011) — the PLATFORM workflow service.  Lands
            // on the all-module catalog list (standing rule 10), from which any
            // workflow opens in the designer.  Sits beside ACT because both
            // configure process; see the ADR on the overlap with ACT's
            // conditional prep-flow rules.
            { kind: 'item', item: { id: 'workflow-engine', label: 'Workflow Engine', icon: 'ti-sitemap' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-accounting', label: 'Accounting', icon: 'ti-calculator',
          children: [
            { kind: 'item', item: { id: 'acc-paymentterms',       label: 'Payment Terms',       icon: 'ti-cash' } },
            { kind: 'item', item: { id: 'acc-paymentterms-setup', label: 'Payment Terms Setup', icon: 'ti-receipt' } },
            { kind: 'item', item: { id: 'acc-region',             label: 'Region',              icon: 'ti-map-pin' } },
          ],
      }},
      // Settings ▸ Utility ▸ Test master-data lookups (2026-06-30) — Container,
      // Preservative, Temperature, QC Type, QC Role (legacy dbo.* tables; ModuleListView).
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-test', label: 'Test', icon: 'ti-flask',
          children: [
            { kind: 'item', item: { id: 'set-container',    label: 'Container',    icon: 'ti-box' } },
            { kind: 'item', item: { id: 'set-method-category', label: 'Method Category', icon: 'ti-category' } },
            { kind: 'item', item: { id: 'set-preservative', label: 'Preservative', icon: 'ti-droplet' } },
            { kind: 'item', item: { id: 'set-qctype',       label: 'QC Type',      icon: 'ti-checkbox' } },
            { kind: 'item', item: { id: 'set-qcrole',       label: 'QC Role',      icon: 'ti-user-cog' } },
            { kind: 'item', item: { id: 'set-qc-root-role', label: 'QC Root Role', icon: 'ti-hierarchy-2' } },
            { kind: 'item', item: { id: 'set-qc-source',    label: 'QC Source',    icon: 'ti-source-code' } },
            { kind: 'item', item: { id: 'set-sample-category', label: 'Sample Category', icon: 'ti-category-2' } },
            // Sample Field Configuration (legacy VisualMatrix field config) —
            // matrix → per-tab available↔selected fields (2026-08-25).
            { kind: 'item', item: { id: 'set-samplefieldconfig', label: 'Sample Field Configuration', icon: 'ti-forms' } },
            { kind: 'item', item: { id: 'set-storage',      label: 'Storage',      icon: 'ti-archive' } },
            { kind: 'item', item: { id: 'set-temperature',  label: 'Temperature',  icon: 'ti-temperature' } },
            { kind: 'item', item: { id: 'set-units',        label: 'Units',        icon: 'ti-ruler-2' } },
          ],
      }},
      // Settings ▸ Utility ▸ Analysis ▸ Asbestos master-data lookups + CRM /
      // Client Request / Custom Report Builder / CMDP (merged team package
      // 2026-07-02; legacy dbo.* tables, ModuleListView + SettingsDetailPage via MODULE_CRUD).
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-analysis', label: 'Analysis', icon: 'ti-microscope',
          children: [
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-asbestos', label: 'Asbestos', icon: 'ti-flask-2',
                children: [
                  { kind: 'item', item: { id: 'set-asbestos-fibers',        label: 'Asbestos Fibers',        icon: 'ti-line-dashed' } },
                  { kind: 'item', item: { id: 'set-fiber-types',            label: 'Fiber Type',             icon: 'ti-line' } },
                  { kind: 'item', item: { id: 'set-fiber-type-values',      label: 'Fiber Type Values',      icon: 'ti-table' } },
                  { kind: 'item', item: { id: 'set-materials',              label: 'Materials',              icon: 'ti-box-multiple' } },
                  { kind: 'item', item: { id: 'set-non-asbestos-fibers',    label: 'Non-Asbestos Fibers',    icon: 'ti-line-dashed' } },
                  { kind: 'item', item: { id: 'set-non-fibrous-components', label: 'Non-Fibrous Components',  icon: 'ti-components' } },
                  { kind: 'item', item: { id: 'set-other-components',       label: 'Other Components',       icon: 'ti-puzzle' } },
                  { kind: 'item', item: { id: 'set-result-range',           label: 'Result Range',           icon: 'ti-arrows-horizontal' } },
                  { kind: 'item', item: { id: 'set-unconventional-analysis', label: 'Unconventional Analysis', icon: 'ti-flask-off' } },
                ],
            }},
            // Contract Lab — sub-out contract laboratories (legacy
            // dbo.SubOutContractLab). Placed directly after the Asbestos node
            // under Settings ▸ Analysis (user request 2026-08-24).
            { kind: 'item', item: { id: 'set-contractlab', label: 'Contract Lab', icon: 'ti-building-factory-2' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-crb', label: 'Custom Report Builder', icon: 'ti-layout-grid',
          children: [
            { kind: 'item', item: { id: 'set-report-category', label: 'Report Category', icon: 'ti-category' } },
            { kind: 'item', item: { id: 'set-report-type',     label: 'Report Type',     icon: 'ti-report' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-crm', label: 'CRM', icon: 'ti-address-book',
          children: [
            { kind: 'item', item: { id: 'set-crm-category', label: 'Category', icon: 'ti-category' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-clientreq', label: 'Client Request', icon: 'ti-mail-forward',
          children: [
            { kind: 'item', item: { id: 'set-request-category', label: 'Request Category', icon: 'ti-category' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-util-cmdp', label: 'CMDP', icon: 'ti-clipboard-data',
          children: [
            { kind: 'item', item: { id: 'set-cmdp-sample-category',     label: 'CMDP Sample Category',      icon: 'ti-category-2' } },
            { kind: 'item', item: { id: 'set-cmdp-method-param-match',  label: 'CMDP Method and Param Match', icon: 'ti-list-check' } },
          ],
      }},
      // Analysis Department Chain — per-TestMethod user assignments (merged 2026-06-26).
      { kind: 'item', item: { id: 'adc-chain', label: 'Analysis Department Chain', icon: 'ti-sitemap' } },
      // Global per-form audit-trail config (admin/service) — standing rule 11.
      { kind: 'item', item: { id: 'audittrailcfg', label: 'Audit Trail Configuration', icon: 'ti-history' } },
      // Bottle Sharing — bottle-grouping rules per Sample Matrix / Matrix / Test
      // (legacy dbo.BottleSharing). Placed directly after Audit Trail
      // Configuration under Settings (user request 2026-08-25).
      { kind: 'item', item: { id: 'set-bottlesharing', label: 'Bottle Sharing', icon: 'ti-bottle' } },
      // Custom Report Builder — design/build report templates (admin/service).
      { kind: 'item', item: { id: 'custreportbuilder', label: 'Custom Report Builder', icon: 'ti-layout-grid' } },
      // SDMS — formula configuration (2026-07-28). "Formula Library" = the SHARED
      // definitions (dbo.SpreadSheetBuilder_Formula, used by SDMS templates AND
      // Result Entry); "Result Entry Formula" = the per-Test/Matrix/Method
      // assignment that drives the Result Entry Express calculation.
      { kind: 'subgroup', subgroup: {
          id: 'sg-sdms', label: 'Formula Setup', icon: 'ti-math-function',
          children: [
            { kind: 'item', item: { id: 'reformula',  label: 'Formula Setup',    icon: 'ti-math-function' } },
            { kind: 'item', item: { id: 'formulalib', label: 'Formula Library',      icon: 'ti-math-symbols' } },
          ],
      }},
      // Data Package Review Checklist — checklist items for QC data package
      // review (legacy dbo.DataPackageCheckList). Placed before Default Settings
      // per user request 2026-08-26.
      { kind: 'item', item: { id: 'data-package-checklist', label: 'Data Package Review Checklist', icon: 'ti-clipboard-check' } },
      // Default Settings — a FOLDER, mirroring ALPACA's
      // Settings > Default Settings > ... group (user screenshot 2026-07-25).
      // Only the children that actually exist in Alpaca Intelligence are listed;
      // ALPACA's Job ID Format / Navigation Settings / Date Filter / Report ID
      // Format / Sample Source Setup / Screen Auto Lock have no AI screen yet, so
      // they are deliberately omitted rather than added as dead links.
      { kind: 'subgroup', subgroup: {
          id: 'sg-default-settings', label: 'Default Settings', icon: 'ti-settings-cog',
          children: [
            { kind: 'item', item: { id: 'defaultsettings', label: 'Default Settings',       icon: 'ti-settings-cog' } },
            { kind: 'item', item: { id: 'clientversion',   label: 'Client Version Control', icon: 'ti-license' } },
            { kind: 'item', item: { id: 'alpacaappconfig', label: 'App Config',              icon: 'ti-adjustments-cog' } },
            { kind: 'item', item: { id: 'screenautolock', label: 'Screen Auto Lock',        icon: 'ti-lock-clock' } },
          ],
      }},
      // Global per-form visibility (admin-only).
      { kind: 'item', item: { id: 'formsetup', label: 'Form Config', icon: 'ti-forms' } },
      // Master-data CRUD (ModuleListView).
      { kind: 'item', item: { id: 'set-holidays', label: 'Holidays', icon: 'ti-calendar-event' } },
      { kind: 'item', item: { id: 'set-methods',  label: 'Method',   icon: 'ti-check', iconColor: 'var(--ai-green)' } },
      { kind: 'item', item: { id: 'set-testcode', label: 'Test Code', icon: 'ti-test-pipe' } },
      // Settings ▸ Dashboard ▸ Assign Dashboard To User (ALPACA port).
      { kind: 'item', item: { id: 'set-assign-dashboard', label: 'Assign Dashboard To User', icon: 'ti-layout-dashboard' } },
      // External Data Import — Permit Libraries (limit definitions for the
      // limit-check engine).
      { kind: 'subgroup', subgroup: {
          id: 'sg-permit-libraries', label: 'Permit Libraries', icon: 'ti-shield-half',
          children: [
            { kind: 'item', item: { id: 'edi-permit-types',   label: 'Permit Types',   icon: 'ti-tags' } },
            { kind: 'item', item: { id: 'edi-permit-library', label: 'Permit Library', icon: 'ti-shield-half' } },
            { kind: 'item', item: { id: 'edi-permit-setup',   label: 'Permit Setup',   icon: 'ti-adjustments-alt' } },
          ],
      }},
      // Settings ▸ Permit Library New — the rebuilt Permit Library section
      // (Lifecycle v5 prototype), added 2026-08-27 alongside — NOT replacing —
      // the existing "Permit Libraries" group above, so the in-use External Data
      // Import screens keep working untouched while the new design is reviewed.
      // A sub-group with no children is hidden by AppShell.hasVisibleLeaves, so
      // the group ships with its first form (Permit Type); Limit Type / Permit
      // Limits slot in here as they are built.
      { kind: 'subgroup', subgroup: {
          id: 'sg-permit-library-new', label: 'Permit Library New', icon: 'ti-shield-half',
          children: [
            { kind: 'item', item: { id: 'pln-permit-type', label: 'Permit Type New', icon: 'ti-tags' } },
            // Limit Type — NAV ONLY for now. The Lifecycle v5 prototype introduces
            // this screen, but it has NO backing data: there is no LimitType table or
            // column in the database and no mention of it in ALPACA GoLive (verified
            // 2026-08-27). With no ModuleRegistry entry the shared ModuleListView shows
            // an empty list view with the right title instead of inventing a source.
            { kind: 'item', item: { id: 'pln-limit-type', label: 'Limit Type', icon: 'ti-ruler-2' } },
          ],
      }},
      // External Data Import — feature configuration.  Mail Setup + Service Period
      // are popups on the External Data Import toolbar (ALPACA parity), so only the
      // grid-based config screens are menu items here.
      { kind: 'subgroup', subgroup: {
          id: 'sg-external-data-import', label: 'External Data Import', icon: 'ti-file-import',
          children: [
            { kind: 'item', item: { id: 'edi-sheet-mapping',  label: 'Sheet Mapping',    icon: 'ti-table-options' } },
            { kind: 'item', item: { id: 'edi-limits',         label: 'MCL & SCL Limits', icon: 'ti-ruler-2' } },
          ],
      }},
      // Notification Center — lab alert/notification module, moved under Settings
      // (2026-06-26); alphabetised between Method and Reporting. Reuses the same
      // children (config forms + Browse by Category) it had at the top level.
      { kind: 'subgroup', subgroup: {
          id: NOTIFICATION_CENTER_GROUP.id, label: NOTIFICATION_CENTER_GROUP.label,
          icon: NOTIFICATION_CENTER_GROUP.icon, children: NOTIFICATION_CENTER_GROUP.children ?? [],
      }},
      // Reporting ▸ MOR Template Setup + Regulatory Compliance (alphabetised).
      { kind: 'subgroup', subgroup: {
          id: 'sg-reporting', label: 'Reporting', icon: 'ti-report',
          children: [
            // MOR Report Template Setup (DWQRReportTemplateSetup) — Settings ▸ Reporting.
            { kind: 'item', item: { id: 'mor-template', label: 'MOR Template Setup', icon: 'ti-template' } },
            // Settings ▸ Reporting ▸ MSR — Monthly Summary Repository config
            // forms (merged team package 2026-07-02).
            { kind: 'subgroup', subgroup: {
                id: 'sg-msr', label: 'MSR', icon: 'ti-archive',
                children: [
                  { kind: 'item', item: { id: 'msr-data-import-config', label: 'MSR Data Import Config', icon: 'ti-file-import' } },
                  { kind: 'item', item: { id: 'msr-water-system',       label: 'Water System',           icon: 'ti-droplet' } },
                ],
            }},
            { kind: 'subgroup', subgroup: {
                id: 'sg-regcompliance', label: 'Regulatory Compliance', icon: 'ti-shield-check',
                children: [
                  { kind: 'item', item: { id: 'dwrc-cats',     label: 'Category',                icon: 'ti-category' } },
                  { kind: 'item', item: { id: 'regcompliance', label: 'Category and Test Setup', icon: 'ti-shield-check' } },
                  { kind: 'item', item: { id: 'dwrc-subcats',  label: 'Sub Category',            icon: 'ti-category-2' } },
                ],
            }},
          ],
      }},
      // System Management masters (ALPACA parity, merged 2026-06-26) — Division/Company,
      // Department, Position, State, Country, City, Current Language, Time Zones.
      { kind: 'subgroup', subgroup: {
          id: 'sg-system', label: 'System Management', icon: 'ti-server-cog',
          children: [
            { kind: 'item', item: { id: 'sm-city',       label: 'City',             icon: 'ti-building' } },
            { kind: 'item', item: { id: 'sm-division',   label: 'Division',         icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'sm-division-department', label: 'Division Department', icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'sm-country',    label: 'Country',          icon: 'ti-flag' } },
            { kind: 'item', item: { id: 'sm-language',   label: 'Current Language', icon: 'ti-language' } },
            { kind: 'item', item: { id: 'sm-department', label: 'Department',       icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'sm-glossary',   label: 'Glossary',         icon: 'ti-book' } },
            { kind: 'item', item: { id: 'sm-message-editor', label: 'Message Editor', icon: 'ti-message-2' } },
            { kind: 'item', item: { id: 'sm-position',   label: 'Position',         icon: 'ti-id-badge-2' } },
            { kind: 'item', item: { id: 'sm-state',      label: 'State',            icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'sm-timezones',  label: 'Time Zones',       icon: 'ti-clock-hour-4' } },
          ],
      }},
      // Service-only User Activity.
      { kind: 'item', item: { id: 'useractivity', label: 'User Access Log', icon: 'ti-user-check' } },
      // Service-only Exception Log — application fault tracking (dbo.AI_ExceptionLog).
      { kind: 'item', item: { id: 'exceptionlog', label: 'Exception Log', icon: 'ti-bug' } },
      // App Config (id 'appconfig') stays HIDDEN during the timezone migration
      // (ADR-0008) — restore in alphabetical position when UTC is reinstated.
    ],
  } },
  { kind: 'sep' },
  // ─── User & Security — ALPACA "User and Security" group parity.  Each item
  // is gated by its existing ALPACA NavigationView (see navPermissions.ts) so
  // an admin grants it from the same MAC UI as every legacy screen.  The list
  // forms render through ModuleListView with real ALPACA data. ───
  // Order matches the live ALPACA "User and Security" group (user 2026-07-02):
  // Module Access Control · Roles · Users (ALPACA's HR folder) · Role-Based MAC
  // · My Details.  Navigation Manager is AI-only and stays last.
  { kind: 'group', group: { id: 'grp-user-security', label: 'User & Security', icon: 'ti-shield-lock', items: [
    { id: 'sec-mac',     label: 'Module Access Control', icon: 'ti-lock-cog' },
    { id: 'sec-roles',   label: 'Roles',                 icon: 'ti-id-badge-2' },
    { id: 'sec-users',   label: 'Users',                 icon: 'ti-users' },
    { id: 'sec-rolemac', label: 'Role-Based MAC',        icon: 'ti-table-options' },
    { id: 'mydetails',   label: 'My Details',            icon: 'ti-user-circle' },
    { id: 'sec-navmgr',  label: 'Navigation Manager',    icon: 'ti-list-tree' },
  ] } },
  { kind: 'sep' },
  // ─── SYSTEM group — internal tools (server health, alerts) ───────────────
  // Was a flat `section` header until 2026-06-10; converted to a `group` so it
  // gets the same server icon + collapse chevron as every other nav group.
  { kind: 'group', group: { id: 'grp-system', label: 'System', icon: 'ti-server-2', items: [
    { id: 'servermon',         label: 'Server Monitor', icon: 'ti-server' },
    { id: 'servermon-history', label: 'Server History', icon: 'ti-chart-line' },
    { id: 'emailalerts',       label: 'Email Alerts',   icon: 'ti-mail-cog' },
  ] } },
  { kind: 'sep' },
  // ─── Maintenance — mirrors ALPACA "Maintenance" group (Clients, Contacts,
  // Project, Security).  Wave 1: Security → Version Log migrated first.
  { kind: 'group', group: { id: 'grp-maintenance', label: 'Maintenance', icon: 'ti-tool',
    items: [
      { id: 'maint-versionlog', label: 'Version Log', icon: 'ti-versions', iconColor: 'var(--ai-red)' },
    ],
    children: [
      { kind: 'subgroup', subgroup: {
          id: 'sg-maint-security', label: 'Security', icon: 'ti-shield-lock',
          children: [
            { kind: 'item', item: { id: 'maint-versionlog', label: 'Version Log', icon: 'ti-versions', iconColor: 'var(--ai-red)' } },
          ],
      }},
    ],
  } },
  { kind: 'sep' },
  // ─── Audit Trail Log AI — own top-level module (2026-07-08).  Shows ONLY the
  // audit logs of AI-migrated forms (AI_AUDIT_FORMS in AuditTrailPage.tsx); the
  // Data Center → Audit Trail Log → All Forms view now shows only legacy ALPACA
  // forms.  Routed automatically by AppShell (any `at-*` id → AuditTrailPage). ───
  { kind: 'group', group: { id: 'grp-audittrail-ai', label: 'Audit Trail Log AI', icon: 'ti-history-toggle', items: [
    { id: 'at-ai', label: 'Audit Trail Log AI', icon: 'ti-shield-check' },
  ] } },
  { kind: 'sep' },
  // Data Center — mirrors ALPACA's Data Center group.  "Audit Trail Log" is a
  // collapsible sub-folder INSIDE this group (matching GoLive's nav tree), not a
  // separate top-level group.  The flat `items` list carries every leaf id
  // (DC forms + at-* audit views) so the global nav search still finds them;
  // `children` drives the rendered, nested tree.
  { kind: 'group', group: { id: 'grp-datacenter', label: 'Data Center', icon: 'ti-database',
    items: [  // flat list (search index only) — DC forms first, then audit-trail views
      // Strictly alphabetical by display label (case-insensitive).
      { id: 'dc-collector',              label: 'Client Collectors',            icon: 'ti-users-group' },
      { id: 'dc-compliant',              label: 'Client Complaint',             icon: 'ti-message-report' },
      { id: 'dc-contact',                label: 'Client Contacts',              icon: 'ti-address-book' },
      { id: 'dc-project',                label: 'Client Projects',              icon: 'ti-folder' },
      { id: 'dc2-clients',               label: 'Clients',                      icon: 'ti-building' },
      { id: 'dc-reporting',              label: 'Custom Reports',               icon: 'ti-report' },
      { id: 'dc2-documents',             label: 'Documents',                    icon: 'ti-files' },
      // 2026-06-27: Historic Results hidden from nav — its data (BT + SM archive)
      // is now merged into Result Samples (dc2-resultsamples). Route/page kept.
      { id: 'histres',                   label: 'Historic Results',             icon: 'ti-history' },
      { id: 'dc-invoicing',              label: 'Invoices',                     icon: 'ti-receipt-2' },
      { id: 'dc2-items',                 label: 'Items',                        icon: 'ti-box' },
      { id: 'dc2-jobids',                label: 'Job IDs',                      icon: 'ti-id' },
      { id: 'dc-samplenotes',            label: 'Job Notes',                    icon: 'ti-notes' },
      { id: 'dc-samplelogin',            label: 'Job Samples',                  icon: 'ti-login' },
      { id: 'dc-sampleparam',            label: 'Job Sample Tests',             icon: 'ti-list-details' },
      { id: 'dc2-labware',               label: 'Labware',                      icon: 'ti-flask' },
      { id: 'dc2-methods',               label: 'Methods',                      icon: 'ti-list-check' },
      { id: 'dc-noncon',                 label: 'Non-Conformity Initiation',    icon: 'ti-alert-octagon' },
      { id: 'dc2-paramlibrary',          label: 'Parameter Library',            icon: 'ti-books' },
      { id: 'dc2-preloginparams',        label: 'Pre-Login Sample Parameters',  icon: 'ti-clipboard-text' },
      { id: 'dc2-preloginsettings',      label: 'Pre-Login Settings',           icon: 'ti-settings-2' },
      { id: 'dc2-analyticalbatches',     label: 'QC Batches',                   icon: 'ti-packages' },
      { id: 'dc-qctype',                 label: 'QC Types',                     icon: 'ti-category' },
      { id: 'dc-crmquotes',              label: 'Quotes',                       icon: 'ti-file-dollar' },
      { id: 'dc-resultqc',               label: 'Result QC',                    icon: 'ti-chart-dots' },
      { id: 'dc2-resultsamples',         label: 'Result Samples',               icon: 'ti-flask-2' },
      { id: 'dc-sdmsbatch',              label: 'SDMS Batch Results',           icon: 'ti-table-options' },
      { id: 'dc2-sdmsseq',               label: 'SDMS Sequence Setup',          icon: 'ti-list-numbers' },
      { id: 'dc2-sdmstemplates',         label: 'SDMS Templates',               icon: 'ti-template' },
      { id: 'dc2-testcomponent',         label: 'Test Component',               icon: 'ti-components' },
      { id: 'dc2-testcontainers',        label: 'Test Containers',              icon: 'ti-box-multiple' },
      { id: 'dc2-testpricing',           label: 'Test Default Pricing',         icon: 'ti-currency-dollar' },
      { id: 'dc2-testholdingtime',       label: 'Test Holding Time',            icon: 'ti-hourglass' },
      { id: 'dc2-testinstruments',       label: 'Test Instruments',             icon: 'ti-device-desktop-analytics' },
      { id: 'dc-testparam',              label: 'Test Parameter Defaults',      icon: 'ti-test-pipe-2' },
      { id: 'dc-prepmethods',            label: 'Test Prep Methods',            icon: 'ti-flask-2' },
      { id: 'dc2-testqcparamdefaults',   label: 'Test QC Parameter Defaults',   icon: 'ti-adjustments' },
      { id: 'dc2-tests',                 label: 'Tests',                        icon: 'ti-microscope' },
      { id: 'dc2-users',                 label: 'Users',                        icon: 'ti-users' },
      { id: 'dc-loginlog',               label: 'User Login Tracking',          icon: 'ti-history-toggle' },
      { id: 'dc2-userpermission',        label: 'User Permission',              icon: 'ti-lock-access' },
      { id: 'dc2-userroleright',         label: 'User Role Right',              icon: 'ti-shield-lock' },
      { id: 'dc2-vendors',               label: 'Vendors',                      icon: 'ti-truck' },
      // Audit Trail Log sub-folder views (flat, for search).
      // "All Forms" — the GLOBAL audit view (every form's rows via all=true,
      // standing rule 11 "track all"); leads the folder so it's the first entry.
      { id: 'at-all',            label: 'All Forms',                       icon: 'ti-list-details' },
      // Settings / master-data forms — one audit-log view each (2026-06-27).
      { id: 'at-adcchain',       label: 'Analysis Department Chain',       icon: 'ti-sitemap' },
      { id: 'at-city',           label: 'City',                            icon: 'ti-building' },
      { id: 'at-country',        label: 'Country',                         icon: 'ti-flag' },
      { id: 'at-department',     label: 'Department',                      icon: 'ti-sitemap' },
      { id: 'at-division',       label: 'Division',                        icon: 'ti-building-community' },
      { id: 'at-holidays',       label: 'Holidays',                        icon: 'ti-calendar-event' },
      { id: 'at-method',         label: 'Method',                          icon: 'ti-list-check' },
      { id: 'at-paymentterms',   label: 'Payment Terms',                   icon: 'ti-cash' },
      { id: 'at-paymenttermssetup', label: 'Payment Terms Setup',          icon: 'ti-receipt' },
      { id: 'at-position',       label: 'Position',                        icon: 'ti-id-badge-2' },
      { id: 'at-region',         label: 'Region',                          icon: 'ti-map-pin' },
      { id: 'at-state',          label: 'State',                           icon: 'ti-map-2' },
      { id: 'at-casenarrative',  label: 'Case Narrative',                  icon: 'ti-file-text' },
      { id: 'at-batchreport',    label: 'Batch Reporting',                 icon: 'ti-books' },
      { id: 'at-custreport',     label: 'Custom Reporting',                icon: 'ti-file-analytics' },
      { id: 'at-eddgen',         label: 'EDD Report Generator',            icon: 'ti-database-export' },
      { id: 'at-fielddata',      label: 'Field Data Entry',                icon: 'ti-map-2' },
      { id: 'at-l3subout',       label: 'Level 3 Subout Data Review',      icon: 'ti-checkup-list' },
      { id: 'at-morreport',      label: 'Ad Hoc Reporting',             icon: 'ti-report-analytics' },
      { id: 'at-morpapp',        label: 'MOR Pending Approval',            icon: 'ti-clock-check' },
      { id: 'at-morpdel',        label: 'MOR Pending Delivery',            icon: 'ti-truck-delivery' },
      { id: 'at-openquotes',     label: 'Open Quotes',                     icon: 'ti-file-dollar' },
      { id: 'at-projectnotes',   label: 'Project Notes',                   icon: 'ti-notes' },
      { id: 'at-reptrack',       label: 'Report Tracking',                 icon: 'ti-files' },
      { id: 'at-repval',         label: 'Report Validation',               icon: 'ti-file-check' },
      { id: 'at-rappr',          label: 'Result Approval',                 icon: 'ti-circle-check' },
      { id: 'at-restrack',       label: 'Result Entry',                    icon: 'ti-list-check' },
      { id: 'at-sampreg',        label: 'Sample Registration',             icon: 'ti-clipboard-list' },
      { id: 'at-samplesites',    label: 'Sample Sites',                    icon: 'ti-map-pin' },
      { id: 'at-samptransfer',   label: 'Sample Transfer',                 icon: 'ti-arrows-exchange' },
      { id: 'at-sampaloc',       label: 'Sampling Allocation',             icon: 'ti-grid-dots' },
      { id: 'at-suboutresentry', label: 'Subout Sample Result Entry',      icon: 'ti-table' },
      { id: 'at-suboutresval',   label: 'Subout Sample Result Validation', icon: 'ti-checkup-list' },
      { id: 'at-subouttest',     label: 'Subout Test Order',               icon: 'ti-file-invoice' },
      { id: 'at-tests',          label: 'Tests',                           icon: 'ti-microscope' },
    ],
    children: [
      // "Audit Trail Log" folder sorts first alphabetically (A < C), so it leads
      // the rendered tree, then the DC forms follow in alphabetical order.
      { kind: 'subgroup', subgroup: {
          id: 'sg-audittrail', label: 'Audit Trail Log', icon: 'ti-history-toggle',
          children: [
            // "All Forms" — GLOBAL audit view (every form, all=true); leads the folder.
            { kind: 'item', item: { id: 'at-all',            label: 'All Forms',                       icon: 'ti-list-details' } },
            // Settings / master-data forms — one audit-log view each (2026-06-27).
            { kind: 'item', item: { id: 'at-adcchain',       label: 'Analysis Department Chain',       icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'at-casenarrative',  label: 'Case Narrative',                  icon: 'ti-file-text' } },
            { kind: 'item', item: { id: 'at-city',           label: 'City',                            icon: 'ti-building' } },
            { kind: 'item', item: { id: 'at-country',        label: 'Country',                         icon: 'ti-flag' } },
            { kind: 'item', item: { id: 'at-department',     label: 'Department',                      icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'at-division',       label: 'Division',                        icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'at-holidays',       label: 'Holidays',                        icon: 'ti-calendar-event' } },
            { kind: 'item', item: { id: 'at-method',         label: 'Method',                          icon: 'ti-list-check' } },
            { kind: 'item', item: { id: 'at-paymentterms',   label: 'Payment Terms',                   icon: 'ti-cash' } },
            { kind: 'item', item: { id: 'at-paymenttermssetup', label: 'Payment Terms Setup',          icon: 'ti-receipt' } },
            { kind: 'item', item: { id: 'at-position',       label: 'Position',                        icon: 'ti-id-badge-2' } },
            { kind: 'item', item: { id: 'at-region',         label: 'Region',                          icon: 'ti-map-pin' } },
            { kind: 'item', item: { id: 'at-state',          label: 'State',                           icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'at-batchreport',    label: 'Batch Reporting',                 icon: 'ti-books' } },
            { kind: 'item', item: { id: 'at-custreport',     label: 'Custom Reporting',                icon: 'ti-file-analytics' } },
            { kind: 'item', item: { id: 'at-eddgen',         label: 'EDD Report Generator',            icon: 'ti-database-export' } },
            { kind: 'item', item: { id: 'at-fielddata',      label: 'Field Data Entry',                icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'at-l3subout',       label: 'Level 3 Subout Data Review',      icon: 'ti-checkup-list' } },
            { kind: 'item', item: { id: 'at-morreport',      label: 'Ad Hoc Reporting',             icon: 'ti-report-analytics' } },
            { kind: 'item', item: { id: 'at-morpapp',        label: 'MOR Pending Approval',            icon: 'ti-clock-check' } },
            { kind: 'item', item: { id: 'at-morpdel',        label: 'MOR Pending Delivery',            icon: 'ti-truck-delivery' } },
            { kind: 'item', item: { id: 'at-openquotes',     label: 'Open Quotes',                     icon: 'ti-file-dollar' } },
            { kind: 'item', item: { id: 'at-projectnotes',   label: 'Project Notes',                   icon: 'ti-notes' } },
            { kind: 'item', item: { id: 'at-reptrack',       label: 'Report Tracking',                 icon: 'ti-files' } },
            { kind: 'item', item: { id: 'at-repval',         label: 'Report Validation',               icon: 'ti-file-check' } },
            { kind: 'item', item: { id: 'at-rappr',          label: 'Result Approval',                 icon: 'ti-circle-check' } },
            { kind: 'item', item: { id: 'at-restrack',       label: 'Result Entry',                    icon: 'ti-list-check' } },
            { kind: 'item', item: { id: 'at-sampreg',        label: 'Sample Registration',             icon: 'ti-clipboard-list' } },
            { kind: 'item', item: { id: 'at-samplesites',    label: 'Sample Sites',                    icon: 'ti-map-pin' } },
            { kind: 'item', item: { id: 'at-samptransfer',   label: 'Sample Transfer',                 icon: 'ti-arrows-exchange' } },
            { kind: 'item', item: { id: 'at-sampaloc',       label: 'Sampling Allocation',             icon: 'ti-grid-dots' } },
            { kind: 'item', item: { id: 'at-suboutresentry', label: 'Subout Sample Result Entry',      icon: 'ti-table' } },
            { kind: 'item', item: { id: 'at-suboutresval',   label: 'Subout Sample Result Validation', icon: 'ti-checkup-list' } },
            { kind: 'item', item: { id: 'at-subouttest',     label: 'Subout Test Order',               icon: 'ti-file-invoice' } },
            { kind: 'item', item: { id: 'at-tests',          label: 'Tests',                           icon: 'ti-microscope' } },
          ],
      }},
      { kind: 'item', item: { id: 'dc-collector',            label: 'Client Collectors',            icon: 'ti-users-group' } },
      { kind: 'item', item: { id: 'dc-compliant',            label: 'Client Complaint',             icon: 'ti-message-report' } },
      { kind: 'item', item: { id: 'dc-contact',              label: 'Client Contacts',              icon: 'ti-address-book' } },
      { kind: 'item', item: { id: 'dc-project',              label: 'Client Projects',              icon: 'ti-folder' } },
      { kind: 'item', item: { id: 'dc2-clients',             label: 'Clients',                      icon: 'ti-building' } },
      { kind: 'item', item: { id: 'dc-reporting',            label: 'Custom Reports',               icon: 'ti-report' } },
      { kind: 'item', item: { id: 'dc2-documents',           label: 'Documents',                    icon: 'ti-files' } },
      // 2026-06-27: Historic Results hidden from nav — merged into Result Samples (dc2-resultsamples).
      { kind: 'item', item: { id: 'histres',                 label: 'Historic Results',             icon: 'ti-history' } },
      { kind: 'item', item: { id: 'dc-invoicing',            label: 'Invoices',                     icon: 'ti-receipt-2' } },
      { kind: 'item', item: { id: 'dc2-items',               label: 'Items',                        icon: 'ti-box' } },
      { kind: 'item', item: { id: 'dc2-jobids',              label: 'Job IDs',                      icon: 'ti-id' } },
      { kind: 'item', item: { id: 'dc-samplenotes',          label: 'Job Notes',                    icon: 'ti-notes' } },
      { kind: 'item', item: { id: 'dc-samplelogin',          label: 'Job Samples',                  icon: 'ti-login' } },
      { kind: 'item', item: { id: 'dc-sampleparam',          label: 'Job Sample Tests',             icon: 'ti-list-details' } },
      { kind: 'item', item: { id: 'dc2-labware',             label: 'Labware',                      icon: 'ti-flask' } },
      { kind: 'item', item: { id: 'dc2-methods',             label: 'Methods',                      icon: 'ti-list-check' } },
      { kind: 'item', item: { id: 'dc-noncon',               label: 'Non-Conformity Initiation',    icon: 'ti-alert-octagon' } },
      { kind: 'item', item: { id: 'dc2-paramlibrary',        label: 'Parameter Library',            icon: 'ti-books' } },
      { kind: 'item', item: { id: 'dc2-preloginparams',      label: 'Pre-Login Sample Parameters',  icon: 'ti-clipboard-text' } },
      { kind: 'item', item: { id: 'dc2-preloginsettings',    label: 'Pre-Login Settings',           icon: 'ti-settings-2' } },
      { kind: 'item', item: { id: 'dc2-analyticalbatches',   label: 'QC Batches',                   icon: 'ti-packages' } },
      { kind: 'item', item: { id: 'dc-qctype',               label: 'QC Types',                     icon: 'ti-category' } },
      { kind: 'item', item: { id: 'dc-crmquotes',            label: 'Quotes',                       icon: 'ti-file-dollar' } },
      { kind: 'item', item: { id: 'dc-resultqc',             label: 'Result QC',                    icon: 'ti-chart-dots' } },
      { kind: 'item', item: { id: 'dc2-resultsamples',       label: 'Result Samples',               icon: 'ti-flask-2' } },
      { kind: 'item', item: { id: 'dc-sdmsbatch',            label: 'SDMS Batch Results',           icon: 'ti-table-options' } },
      { kind: 'item', item: { id: 'dc2-sdmsseq',             label: 'SDMS Sequence Setup',          icon: 'ti-list-numbers' } },
      { kind: 'item', item: { id: 'dc2-sdmstemplates',       label: 'SDMS Templates',               icon: 'ti-template' } },
      { kind: 'item', item: { id: 'dc2-testcomponent',       label: 'Test Component',               icon: 'ti-components' } },
      { kind: 'item', item: { id: 'dc2-testcontainers',      label: 'Test Containers',              icon: 'ti-box-multiple' } },
      { kind: 'item', item: { id: 'dc2-testpricing',         label: 'Test Default Pricing',         icon: 'ti-currency-dollar' } },
      { kind: 'item', item: { id: 'dc2-testholdingtime',     label: 'Test Holding Time',            icon: 'ti-hourglass' } },
      { kind: 'item', item: { id: 'dc2-testinstruments',     label: 'Test Instruments',             icon: 'ti-device-desktop-analytics' } },
      { kind: 'item', item: { id: 'dc-testparam',            label: 'Test Parameter Defaults',      icon: 'ti-test-pipe-2' } },
      { kind: 'item', item: { id: 'dc-prepmethods',          label: 'Test Prep Methods',            icon: 'ti-flask-2' } },
      { kind: 'item', item: { id: 'dc2-testqcparamdefaults', label: 'Test QC Parameter Defaults',   icon: 'ti-adjustments' } },
      { kind: 'item', item: { id: 'dc2-tests',               label: 'Tests',                        icon: 'ti-microscope' } },
      { kind: 'item', item: { id: 'dc2-users',               label: 'Users',                        icon: 'ti-users' } },
      { kind: 'item', item: { id: 'dc-loginlog',             label: 'User Login Tracking',          icon: 'ti-history-toggle' } },
      { kind: 'item', item: { id: 'dc2-userpermission',      label: 'User Permission',              icon: 'ti-lock-access' } },
      { kind: 'item', item: { id: 'dc2-userroleright',       label: 'User Role Right',              icon: 'ti-shield-lock' } },
      { kind: 'item', item: { id: 'dc2-vendors',             label: 'Vendors',                      icon: 'ti-truck' } },
    ],
  } },
];

// ─── Settings: alphabetical ordering ────────────────────────────────────────
// The Settings group grew to ~19 sub-folders and dozens of loose forms added in
// whatever order each feature landed, so the tree read as (Analysis, Reporting,
// Accounting, Test, Asbestos, …) — no order at all, despite a comment claiming
// alphabetical.  With that many entries the only findable order is A→Z, so it is
// applied HERE, once, rather than by hand-placing each new form: a list kept
// sorted by convention drifts the first time someone appends instead of inserts.
//
// Folders and loose forms sort TOGETHER by their visible label, matching how the
// sidebar draws them (a folder is not "more important" than a form, just
// expandable).  Sorting is case-insensitive and numeric-aware so "Test 2" sorts
// after "Test 1", not between "Test 1" and "Test 10".
//
// Applies ONLY to Settings — no other group's order is touched.  The admin
// form-sort (sortItems / sortChildren in AppShell) still overrides this when
// configured, so a lab can still pin its own order on top.
{
  const label = (c: NavGroupChild): string =>
    c.kind === 'item' ? c.item.label : c.kind === 'subgroup' ? c.subgroup.label : '';
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  // TOP LEVEL ONLY — deliberately not recursive.
  //
  // A first attempt sorted nested folders too and broke Default Settings, where
  // App Config sits directly beneath Client Version Control to mirror ALPACA's
  // own tree (appConfigNavVisibility.test.ts asserts that adjacency, and it
  // failed immediately).  Inside a folder the grouping is small enough to read
  // and the order often carries meaning; it is the ~19-entry top level that is
  // unnavigable unsorted.  So: sort what the user scans, leave curated runs
  // alone.
  const sortTopLevel = (children: NavGroupChild[]): NavGroupChild[] => {
    // `subhead` markers label the run of items beneath them, so a tree using
    // them cannot be flat-sorted without detaching a heading from its group.
    // Settings has none today; bail out rather than silently scramble one if a
    // later change introduces them.
    if (children.some(c => c.kind === 'subhead')) return children;
    return [...children].sort((a, b) => collator.compare(label(a), label(b)));
  };

  const settings = navTree.find(e => e.kind === 'group' && e.group.id === 'grp-settings');
  if (settings && settings.kind === 'group') {
    if (settings.group.children) {
      settings.group.children = sortTopLevel(settings.group.children);
    }
    // The flat `items` list backs global nav search; sorting it keeps the
    // search results in the same order as the menu.
    settings.group.items = [...settings.group.items]
      .sort((a, b) => collator.compare(a.label, b.label));
  }
}

/** Flat lookup: page id -> display label (for breadcrumb/title). */
export const pageTitles: Record<string, string> = (() => {
  const map: Record<string, string> = { home: 'Home' };
  for (const e of navTree) {
    if (e.kind === 'item') map[e.item.id] = e.item.label;
    if (e.kind === 'group') for (const it of e.group.items) map[it.id] = it.label;
  }
  return map;
})();

/** Flat lookup: page id -> breadcrumb group (section or group label). */
export const pageGroups: Record<string, string> = (() => {
  const map: Record<string, string> = { home: 'Overview' };
  let section = 'Overview';
  for (const e of navTree) {
    if (e.kind === 'section') section = e.label;
    if (e.kind === 'item') map[e.item.id] = section;
    if (e.kind === 'group') for (const it of e.group.items) map[it.id] = e.group.label;
  }
  return map;
})();

/** Flat lookup: page id -> its top-level nav GROUP id (e.g. 'grp-backlog').
 *  Used to auto-expand the group that contains the currently-open form so the
 *  user can see which form is active.  Walks both the flat `items` list and the
 *  nested `children` tree so every leaf page resolves. */
export const pageGroupId: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (children: NavGroupChild[] | undefined, groupId: string) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') map[c.item.id] = groupId;
      else if (c.kind === 'subgroup') walk(c.subgroup.children, groupId);
    }
  };
  for (const e of navTree) {
    if (e.kind === 'group') {
      for (const it of e.group.items) map[it.id] = e.group.id;
      walk(e.group.children, e.group.id);
    }
  }
  return map;
})();

/** Nav metadata for one page id: its label, icon and (optional) colour. */
export interface NavMeta { id: string; label: string; icon: string; iconColor?: string; }

/** Flat lookup: page id -> { label, icon, iconColor } — the SINGLE source of
 *  truth for a form's title text, icon and colour (CLAUDE.md §18.6 task 3).
 *  Every form's on-page title (`<FormTitle pageId=…>`) reads from here, so the
 *  title text / icon / colour above a grid can never drift from that form's
 *  sidebar entry.  Mirrors exactly how AppShell renders a nav item (icon class
 *  + optional inline iconColor), so the glyph reproduces the nav glyph 1:1.
 *  First occurrence wins — the flat `items` lists (which carry iconColor) are
 *  walked before the nested `children`. */
export const navMetaById: Record<string, NavMeta> = (() => {
  const map: Record<string, NavMeta> = { home: { id: 'home', label: 'Home', icon: 'ti-home' } };
  const add = (it: NavItem) => {
    if (!map[it.id]) map[it.id] = { id: it.id, label: it.label, icon: it.icon, iconColor: it.iconColor };
  };
  const walk = (children: NavGroupChild[] | undefined) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') add(c.item);
      else if (c.kind === 'subgroup') walk(c.subgroup.children);
    }
  };
  for (const e of navTree) {
    if (e.kind === 'item') add(e.item);
    if (e.kind === 'group') { for (const it of e.group.items) add(it); walk(e.group.children); }
  }
  return map;
})();

/** Runtime nav-caption overrides — page id → caption.  Populated by AppShell from
 *  `GET /api/v1/navigation/captions` (the live `dbo.NavigationItem.NavigationCaption`
 *  column).  Empty by default, so the curated labels above are the fallback and nothing
 *  changes until an admin renames a node in the Navigation Manager (CLAUDE.md §0d). */
let _captionOverrides: Record<string, string> = {};

/** Replace the runtime caption-override map (AppShell calls this after fetching the feed). */
export function setNavCaptionOverrides(map: Record<string, string>): void {
  _captionOverrides = map ?? {};
}

/** Resolve a page id to its display label, honouring an admin's runtime rename.
 *  Order: runtime override → curated nav label → page-title map → fallback → id. */
export function navLabel(id: string, fallback?: string): string {
  return _captionOverrides[id] ?? navMetaById[id]?.label ?? pageTitles[id] ?? fallback ?? id;
}

/** The CURATED (code) label for a page id, ignoring any runtime override.  AppShell
 *  uses this as the baseline when deciding whether a DB caption is an admin rename
 *  (DB caption differs from the curated label → override the sidebar). */
export function rawNavLabel(id: string): string | undefined {
  return navMetaById[id]?.label ?? pageTitles[id];
}

/** Normalise a module label / name for matching a sidebar group to a DB module
 *  (lower-case, "&" → "and", collapse whitespace) — e.g. "User & Security" ↔ "User and Security". */
export const normMod = (s: string) => s.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();

/** Aliases for modules whose DB name differs from the sidebar label, so both canonicalise
 *  to the SAME key (normalised).  Extend as mismatches surface. */
const MODULE_ALIASES: Record<string, string> = {
  // DB "Sample SubOut Tracking" ↔ sidebar "Sample SubContract Tracking"
  'sample subout tracking': 'sample subcontract tracking',
};

/** Canonical module key for matching a sidebar group to a DB module — `normMod` + alias map. */
export const canonMod = (s: string) => { const n = normMod(s); return MODULE_ALIASES[n] ?? n; };

/** All leaf (clickable item) page ids under a group — its flat `items` plus every
 *  item nested in its `children` sub-groups.  AppShell uses these to resolve a group
 *  header's DB caption from one of its child items' Parent (CLAUDE.md §0d). */
export function collectGroupLeafIds(group: NavGroup): string[] {
  const ids: string[] = group.items.map(it => it.id);
  const walk = (children: NavGroupChild[] | undefined) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') ids.push(c.item.id);
      else if (c.kind === 'subgroup') walk(c.subgroup.children);
    }
  };
  walk(group.children);
  return ids;
}

/** Resolve a page id to its nav label/icon/colour (undefined when unknown,
 *  e.g. a hidden form not in the sidebar).  The label folds in any runtime
 *  caption override so `<FormTitle>` reflects a renamed node too. */
export function navMeta(id: string): NavMeta | undefined {
  const m = navMetaById[id];
  if (m) return _captionOverrides[id] ? { ...m, label: _captionOverrides[id] } : m;
  return _captionOverrides[id] ? { id, label: _captionOverrides[id], icon: 'ti-table' } : undefined;
}

/** The "Audit Trail Log AI" group id + its consolidated (All AI Forms) leaf. */
const AUDIT_AI_GROUP_ID = 'grp-audittrail-ai';
const AUDIT_AI_ALL_ITEM: NavItem = { id: 'at-ai', label: 'Audit Trail Log AI', icon: 'ti-shield-check' };

/** Populate the "Audit Trail Log AI" nav group with ONE leaf PER AI form that has
 *  audit data — so the sidebar itself is the coverage inventory (user request
 *  2026-07-10: "create each form each navigation … then only i know which form has
 *  audit trail in AI").  Called by AppShell after it fetches the forms-with-data
 *  from /api/v1/audit/forms.  The consolidated "Audit Trail Log AI" (at-ai) leaf is
 *  always kept FIRST, then the per-form leaves in the given order.  Idempotent —
 *  each call REPLACES the group's items (so a form that loses all its rows drops
 *  off).  Mutates navTree + the derived lookups in place; AppShell bumps a render
 *  tick so the sidebar re-reads them (orderedTree returns navTree by reference). */
export function setAiAuditFormNav(forms: { id: string; label: string; count?: number }[]): void {
  const entry = navTree.find(e => e.kind === 'group' && e.group.id === AUDIT_AI_GROUP_ID);
  if (!entry || entry.kind !== 'group') return;
  const g = entry.group;
  const items: NavItem[] = [
    AUDIT_AI_ALL_ITEM,
    ...forms.map(f => ({ id: f.id, label: f.label, icon: 'ti-shield-check', count: f.count })),
  ];
  g.items = items;
  // Register the derived lookups for the dynamic ids so labels, breadcrumbs,
  // group auto-expand and <FormTitle> all resolve (they're computed once from the
  // static tree, so new ids must be added here explicitly).
  for (const it of items) {
    pageTitles[it.id]   = it.label;
    pageGroups[it.id]   = g.label;
    pageGroupId[it.id]  = g.id;
    navMetaById[it.id]  = { id: it.id, label: it.label, icon: it.icon };
  }
}
